import { ComponentType, useMemo, useState } from "react";
import {
  X,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  UserPlus,
  UserMinus,
  ArrowRightLeft,
  Flag,
  Ban,
  RotateCcw,
  Plus,
  Pencil,
  Trash2,
  Lock,
  Unlock,
  Sparkles,
  Activity,
} from "lucide-react";
import {
  useAdminAuditLogs,
  AuditLogFilters,
} from "../api/useAdminAuditLogs";
import { AdminAPI } from "@shared/types/api";
import { TableSearchInput } from "./TableSearchInput";

type IconType = ComponentType<{ className?: string }>;

interface ActionMeta {
  label: string;
  icon: IconType;
  // Tailwind classes for the icon chip background and foreground.
  tone: string;
}

// Keep in sync with AUDIT_ACTIONS in server/src/services/auditLog.ts.
const ACTION_META: Record<string, ActionMeta> = {
  "assignment.create": {
    label: "Assignment created",
    icon: UserPlus,
    tone: "bg-emerald-500/15 text-emerald-400",
  },
  "assignment.move": {
    label: "Assignment moved",
    icon: ArrowRightLeft,
    tone: "bg-sky-500/15 text-sky-400",
  },
  "assignment.remove": {
    label: "Assignment removed",
    icon: UserMinus,
    tone: "bg-rose-500/15 text-rose-400",
  },
  "assignment.flag": {
    label: "Assignment flagged",
    icon: Flag,
    tone: "bg-amber-500/15 text-amber-400",
  },
  "booking.cant_be_seated": {
    label: "Marked can't be seated",
    icon: Ban,
    tone: "bg-rose-500/15 text-rose-400",
  },
  "booking.revived": {
    label: "Revived from can't be seated",
    icon: RotateCcw,
    tone: "bg-emerald-500/15 text-emerald-400",
  },
  "table.create": {
    label: "Table created",
    icon: Plus,
    tone: "bg-emerald-500/15 text-emerald-400",
  },
  "table.update": {
    label: "Table updated",
    icon: Pencil,
    tone: "bg-base-content/15 text-base-content/80",
  },
  "table.delete": {
    label: "Table deleted",
    icon: Trash2,
    tone: "bg-rose-500/15 text-rose-400",
  },
  "table.lock": {
    label: "Table locked",
    icon: Lock,
    tone: "bg-purple-500/15 text-purple-400",
  },
  "table.unlock": {
    label: "Table unlocked",
    icon: Unlock,
    tone: "bg-purple-500/15 text-purple-400",
  },
  "matching.run": {
    label: "Auto-match run",
    icon: Sparkles,
    tone: "bg-primary-500/15 text-primary-400",
  },
};

const FALLBACK_META: ActionMeta = {
  label: "Unknown action",
  icon: Activity,
  tone: "bg-base-content/15 text-base-content/60",
};

const ACTION_OPTIONS = [
  { value: "", label: "All actions" },
  ...Object.entries(ACTION_META).map(([value, meta]) => ({
    value,
    label: meta.label,
  })),
];

interface AuditLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Filters applied on open. The user can additionally narrow by action. */
  baseFilters: AuditLogFilters;
  /** Friendly heading describing the scope (e.g., event date, user name). */
  contextLabel?: string;
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function describeEvent(entry: AdminAPI.AuditLogEntry): string | null {
  if (!entry.eventDate) return null;
  return entry.eventTime
    ? `${entry.eventDate} ${entry.eventTime}`
    : entry.eventDate;
}

function describeTable(entry: AdminAPI.AuditLogEntry): string | null {
  if (!entry.availableTableId && !entry.tableReservationName) return null;
  const parts = [entry.restaurantName, entry.tableReservationName].filter(
    Boolean
  );
  return parts.join(" — ") || entry.availableTableId;
}

function getActorLabel(entry: AdminAPI.AuditLogEntry): string | null {
  if (entry.actorName) return entry.actorName;
  const meta = entry.metadata as Record<string, unknown> | null;
  if (meta && meta.actor === "mcp") return "MCP";
  return null;
}

export function AuditLogModal({
  isOpen,
  onClose,
  baseFilters,
  contextLabel,
}: AuditLogModalProps) {
  const [actionFilter, setActionFilter] = useState<string>("");
  const [search, setSearch] = useState<string>("");

  const filters = useMemo<AuditLogFilters>(
    () => ({
      ...baseFilters,
      action: actionFilter || undefined,
    }),
    [baseFilters, actionFilter]
  );

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useAdminAuditLogs(filters, isOpen);

  const entries = useMemo(
    () => data?.pages.flatMap((p) => p.data?.entries ?? []) ?? [],
    [data]
  );

  // Client-side fuzzy filter across loaded entries. Searches action label,
  // actor / subject / table / restaurant / event strings, and the raw
  // metadata JSON so adminIds, table ids, rationales etc. are findable.
  // The "Load more" button still expands the search range.
  const normalizedSearch = search.trim().toLowerCase();
  const visibleEntries = useMemo(() => {
    if (!normalizedSearch) return entries;
    return entries.filter((entry) => {
      const meta = ACTION_META[entry.action] ?? FALLBACK_META;
      const parts: (string | null | undefined)[] = [
        meta.label,
        entry.action,
        entry.actorName,
        getActorLabel(entry),
        entry.subjectName,
        entry.restaurantName,
        entry.tableReservationName,
        entry.availableTableId,
        entry.bookingId,
        entry.subjectUserId,
        entry.eventDate,
        entry.eventTime,
        entry.metadata ? JSON.stringify(entry.metadata) : null,
      ];
      const haystack = parts
        .filter((v): v is string => Boolean(v))
        .join("\n")
        .toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [entries, normalizedSearch]);

  // Used to suppress redundant rows in each entry. When the modal is opened
  // scoped to a single event or user, that field is the same on every entry.
  const scopedByEvent = !!baseFilters.eventTimeId;
  const scopedBySubject = !!baseFilters.subjectUserId;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-base-100 rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-base-300">
          <div className="min-w-0">
            <h2 className="text-lg font-bold">Audit log</h2>
            <p className="text-base-content/60 text-xs truncate">
              {contextLabel || "All admin actions"}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => refetch()}
              className="btn btn-ghost btn-xs btn-circle w-8 h-8 p-1"
              disabled={isFetching}
              title="Refresh"
            >
              <RefreshCw
                className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`}
              />
            </button>
            <button
              onClick={onClose}
              className="btn btn-ghost btn-xs btn-circle w-8 h-8 p-1"
            >
              <X width={20} height={20} />
            </button>
          </div>
        </div>

        <div className="px-4 py-3 border-b border-base-300 flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-base-content/60">
            Action
          </span>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="select select-bordered select-sm"
          >
            {ACTION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <TableSearchInput
            value={search}
            onDebouncedChange={setSearch}
            debounceMs={250}
            placeholder="Search actor, table, metadata…"
            className="relative w-full sm:w-64"
          />
          <span className="text-xs text-base-content/40 ml-auto">
            {normalizedSearch ? (
              <>
                {visibleEntries.length} of {entries.length}
                {hasNextPage ? "+" : ""}
              </>
            ) : (
              <>
                {entries.length}{" "}
                {entries.length === 1 ? "entry" : "entries"}
                {hasNextPage ? "+" : ""}
              </>
            )}
          </span>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <span className="loading loading-spinner loading-md text-primary" />
            </div>
          ) : visibleEntries.length === 0 ? (
            <div className="text-center py-16 text-base-content/60 text-sm">
              {normalizedSearch
                ? `No loaded entries match "${search.trim()}"${
                    hasNextPage ? " — load more to broaden the search." : "."
                  }`
                : "No audit entries match these filters yet."}
            </div>
          ) : (
            <ul className="divide-y divide-base-300">
              {visibleEntries.map((entry) => (
                <AuditLogRow
                  key={entry.id}
                  entry={entry}
                  scopedByEvent={scopedByEvent}
                  scopedBySubject={scopedBySubject}
                />
              ))}
            </ul>
          )}
        </div>

        {hasNextPage && (
          <div className="px-4 py-3 border-t border-base-300 flex justify-center">
            <button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="btn btn-sm btn-ghost"
            >
              {isFetchingNextPage ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function AuditLogRow({
  entry,
  scopedByEvent,
  scopedBySubject,
}: {
  entry: AdminAPI.AuditLogEntry;
  scopedByEvent: boolean;
  scopedBySubject: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  const meta = ACTION_META[entry.action] ?? FALLBACK_META;
  const Icon = meta.icon;
  const tableText = describeTable(entry);
  const eventText = scopedByEvent ? null : describeEvent(entry);
  const subjectText = scopedBySubject ? null : entry.subjectName;
  const actorLabel = getActorLabel(entry);
  const hasMetadata =
    entry.metadata != null &&
    typeof entry.metadata === "object" &&
    Object.keys(entry.metadata).length > 0;

  return (
    <li className="px-4 py-2.5 hover:bg-base-200/40">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-start gap-3 text-left"
      >
        <span
          className={`mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-md shrink-0 ${meta.tone}`}
        >
          <Icon className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-semibold">{meta.label}</span>
            {actorLabel && (
              <span className="text-xs text-base-content/60">
                by {actorLabel}
              </span>
            )}
            <span className="text-xs text-base-content/50">
              {formatShortDate(entry.createdAt)}
            </span>
          </div>
          {(subjectText || tableText || eventText) && (
            <div className="text-xs text-base-content/70 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
              {subjectText && (
                <span>
                  <span className="text-base-content/40">Subject </span>
                  {subjectText}
                </span>
              )}
              {tableText && (
                <span>
                  <span className="text-base-content/40">Table </span>
                  {tableText}
                </span>
              )}
              {eventText && (
                <span>
                  <span className="text-base-content/40">Event </span>
                  {eventText}
                </span>
              )}
            </div>
          )}
        </div>
        <span className="mt-1 text-base-content/40">
          {expanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </span>
      </button>

      {expanded && (
        <div className="mt-2 ml-10 text-xs space-y-1 text-base-content/70">
          {scopedByEvent && describeEvent(entry) && (
            <div>
              <span className="text-base-content/40">Event: </span>
              {describeEvent(entry)}
            </div>
          )}
          {scopedBySubject && entry.subjectName && (
            <div>
              <span className="text-base-content/40">Subject: </span>
              {entry.subjectName}
            </div>
          )}
          {hasMetadata && (
            <pre className="mt-1 text-[11px] font-mono bg-base-200 rounded p-2 overflow-x-auto whitespace-pre-wrap">
              {JSON.stringify(entry.metadata, null, 2)}
            </pre>
          )}
        </div>
      )}
    </li>
  );
}
